import { Environment, isValidEnv } from '../environments';
import { buildUri } from './utils';

export const Endpoint = {

    /**
     * Our back-end GraphQL server used to handle most of our queries and mutations.
     */
     bunchball: {
        /**
         * A label describing what the endpoint is used for.
         * @type { string }
         */
        label: 'Bunchball Server',

        /**
         * The original URL/path before any variables have been substituted in. This value
         * may be a relative path.
         * @type { string }
         */
        path: 'https://api.(sandbox.)bunchball.com',

        /**
         * Builds the endpoint URL using the provided parameters.
         * 
         * @param { string } env - The environment to retrieve from.
         * @param { boolean } canAttemptFallback - Allows for an attempted fallback if, for example, an environment is not valid.
         * @returns { string }
         */
        build: ( env, canAttemptFallback = false ) => {
            let finalEnv = env;
            // Decide whether or not to fail the build request if necessary information
            // is missing or considered to be invalid.
            const validEnv = isValidEnv( env );
            if ( !validEnv ) {
                console.error( `The request to build the '${ Endpoint.bunchball.label }' endpoint has failed.` );
                return undefined;
            }

            // Validate environment value.
            if ( validEnv ) {
                finalEnv = env;
            } else if ( canAttemptFallback ) {
                const failedBuildMessage = `Cannot build '${ Endpoint.bunchball.label }' endpoint using invalid environment '${ env }'.`;
                finalEnv = Environment.QA;
                console.warn( `${ failedBuildMessage } Falling back to '${ finalEnv }'.` );
            }
            // Build the endpoint URL replacing any possible variables in the string.
            return buildUri( Endpoint.bunchball.path, {
                sandbox: ( [ Environment.PROD, Environment.PPRD ].includes( finalEnv ) ) ? '' : 'sandbox',
            } );
        }
    },

    /**
     * Our back-end GraphQL server used to handle most of our queries and mutations.
     */
    graphqlServer: {
        /**
         * A label describing what the endpoint is used for.
         * @type { string }
         */
        label: 'GraphQL Server',

        /**
         * The original URL/path before any variables have been substituted in. This value
         * may be a relative path.
         * @type { string }
         */
        // path: 'https://(env)-hoya-graphql.biwlearning-hoya.com/graphql',
        path: 'https://(env.)api.biw.cloud/v1/learning-exp/graphql',

        /**
         * Builds the endpoint URL using the provided parameters.
         * 
         * @param { string } env - The environment to retrieve from.
         * @param { boolean } canAttemptFallback - Allows for an attempted fallback if, for example, an environment is not valid.
         * @returns { string }
         */
        build: ( env, canAttemptFallback = false ) => {
            let finalEnv = env;

            // Check if the environment is local. If so, return a unique endpoint to point
            // at the local GraphQL server.
            if ( env === Environment.LOCAL ) {
                return 'http://localhost:4000/';
            }

            // Decide whether or not to fail the build request if necessary information
            // is missing or considered to be invalid.
            const validEnv = isValidEnv( env );
            if ( !validEnv ) {
                console.error( `The request to build the '${ Endpoint.graphqlServer.label }' endpoint has failed.` );
                return undefined;
            }

            // Validate environment value.
            if ( validEnv ) {
                finalEnv = env;
            } else if ( canAttemptFallback ) {
                const failedBuildMessage = `Cannot build '${ Endpoint.graphqlServer.label }' endpoint using invalid environment '${ env }'.`;
                finalEnv = Environment.QA;
                console.warn( `${ failedBuildMessage } Falling back to '${ finalEnv }'.` );
            }

            // Build the endpoint URL replacing any possible variables in the string.
            return buildUri( Endpoint.graphqlServer.path, {
                env: ( finalEnv !== Environment.PROD ) ? finalEnv : '',
            } );
        }
    },

    /**
     * The API root. Used in the building of some of the other endpoints.
     */
    apiRoot: {
        /**
         * A label describing what the endpoint is used for.
         * @type { string }
         */
        label: 'API Root',

        /**
         * The original URL/path before any variables have been substituted in. This value
         * may be a relative path.
         * @type { string }
         */
        path: 'https://(env.)api.biw.cloud',

        /**
         * Builds the endpoint URL using the provided parameters.
         * 
         * @param { string } env - The environment to retrieve from.
         * @param { boolean } canAttemptFallback - Allows for an attempted fallback if, for example, an environment is not valid.
         * @returns { string }
         */
        build: ( env, canAttemptFallback = false ) => {
            let finalEnv = env;

            // Decide whether or not to fail the build request if necessary information
            // is missing or considered to be invalid.
            const validEnv = ( isValidEnv( env ) && env !== Environment.DEV );
            if ( !validEnv && !canAttemptFallback ) {
                console.error( `The request to build the '${ Endpoint.apiRoot.label }' endpoint has failed.` );
                return undefined;
            }

            // Validate environment value.
            if ( validEnv ) {
                finalEnv = env;
            } else {
                const failedBuildMessage = `Cannot build '${ Endpoint.apiRoot.label }' endpoint using invalid environment '${ env }'.`;
                finalEnv = Environment.QA;
                console.warn( `${ failedBuildMessage } Falling back to '${ finalEnv }'.` );
            }

            // Build the endpoint URL replacing any possible variables in the string.
            return buildUri( Endpoint.apiRoot.path, {
                env: ( finalEnv !== Environment.PROD ) ? finalEnv : '',
            } );
        }
    },

    /**
     * The endpoint of our ID service.
     */
    id: {
        /**
         * A label describing what the endpoint is used for.
         * @type { string }
         */
        label: 'ID Service',

        /**
         * The original URL/path before any variables have been substituted in. This value
         * may be a relative path.
         * @type { string }
         */
        path: '/v1/id',

        /**
         * Builds the endpoint URL using the provided parameters.
         * 
         * @param { string } env - The environment to retrieve from.
         * @param { boolean } canAttemptFallback - Allows for an attempted fallback if, for example, an environment is not valid.
         * @returns { string }
         */
        build: ( env, canAttemptFallback = false ) => {
            // Decide whether or not to fail the build request if necessary information
            // is missing or considered to be invalid.
            const validEnv = ( isValidEnv( env ) && env !== Environment.DEV );
            if ( !validEnv && !canAttemptFallback ) {
                console.error( `The request to build the '${ Endpoint.id.label }' endpoint has failed.` );
                return undefined;
            }

            // Build the endpoint URL replacing any possible variables in the string.
            return Endpoint.apiRoot.build( env, canAttemptFallback ) + buildUri( Endpoint.id.path );
        }
    },

    /**
     * The endpoint to retrieve translation bundles for a specific project in CMX.
     */
    translationBundle: {
        /**
         * A label describing what the endpoint is used for.
         * @type { string }
         */
        label: 'Translation Bundle',

        /**
         * The original URL/path before any variables have been substituted in. This value
         * may be a relative path.
         * @type { string }
         */
        path: '/v2/cmx/bundle/(projectKey)',

        /**
         * Builds the endpoint URL using the provided parameters.
         * 
         * @param { string } env - The environment to retrieve from.
         * @param { string } projectKey - The key of the project in CMX to grab from.
         * @param { boolean } canAttemptFallback - Allows for an attempted fallback if, for example, an environment is not valid.
         * @returns { string }
         */
        build: ( env, projectKey, canAttemptFallback = false ) => {
            // Decide whether or not to fail the build request if necessary information
            // is missing or considered to be invalid.
            const validEnv = ( isValidEnv( env ) && env !== Environment.DEV );
            const isValidProjectKey = Boolean( projectKey );
            if ( ( !validEnv && !canAttemptFallback ) || !isValidProjectKey ) {
                console.error( `The request to build the '${ Endpoint.translationBundle.label }' endpoint has failed.` );
                return undefined;
            }

            // Build the endpoint URL replacing any possible variables in the string.
            return Endpoint.apiRoot.build( env, canAttemptFallback ) + buildUri( Endpoint.translationBundle.path, {
                projectKey,
            } );
        }
    },

    /**
     * The endpoint to retrieve the list of locales supported by CMX.
     */
    locales: {
        /**
         * A label describing what the endpoint is used for.
         * @type { string }
         */
        label: 'Locales',

        /**
         * The original URL/path before any variables have been substituted in. This value
         * may be a relative path.
         * @type { string }
         */
        path: '/v2/cmx/locales',

        /**
         * Builds the endpoint URL using the provided parameters.
         * 
         * @param { string } env - The environment to retrieve from.
         * @param { boolean } canAttemptFallback - Allows for an attempted fallback if, for example, an environment is not valid.
         * @returns { string }
         */
        build: ( env, canAttemptFallback = false ) => {
            // Decide whether or not to fail the build request if necessary information
            // is missing or considered to be invalid.
            const validEnv = ( isValidEnv( env ) && env !== Environment.DEV );
            if ( !validEnv && !canAttemptFallback ) {
                console.error( `The request to build the '${ Endpoint.locales.label }' endpoint has failed.` );
                return undefined;
            }

            // Build the endpoint URL replacing any possible variables in the string.
            return Endpoint.apiRoot.build( env, canAttemptFallback ) + buildUri( Endpoint.locales.path );
        }
    },

    /**
     * The endpoint for cloudfront.
     */
    cloudfront: {
        /**
         * A label describing what the endpoint is used for.
         * @type { string }
         */
        label: 'CloudFront',

        /**
         * The original URL/path before any variables have been substituted in. This value
         * may be a relative path.
         * @type { string }
         */
        path: 'http://(env.)biwlearning-hoya.com',

        /**
         * Builds the endpoint URL using the provided parameters.
         * 
         * @param { string } env - The environment to retrieve from.
         * @param { boolean } canAttemptFallback - Allows for an attempted fallback if, for example, an environment is not valid.
         * @returns { string }
         */
        build: ( env, canAttemptFallback = false ) => {
            // Decide whether or not to fail the build request if necessary information
            // is missing or considered to be invalid.
            const validEnv = ( isValidEnv( env ) && env !== Environment.DEV );
            if ( !validEnv && !canAttemptFallback ) {
                console.error( `The request to build the '${ Endpoint.cloudfront.label }' endpoint has failed.` );
                return undefined;
            }

            // Validate environment value.
            if ( env === Environment.LOCAL || env === Environment.UNKNOWN ) {
                const failedBuildMessage = `Cannot build '${ Endpoint.cloudfront.label }' endpoint using invalid environment '${ env }'.`;
                env = Environment.QA;
                console.warn( `${ failedBuildMessage } Falling back to '${ env }'.` );
            }

            let path = Endpoint.cloudfront.path;
            if ( env === Environment.PROD ) {
                path = 'https://www.hoyavisionlearningcenter.com';
            }

            // Build the endpoint URL replacing any possible variables in the string.
            return buildUri( path, { env: ( env !== Environment.PROD ) ? env : '' } );
        }
    },
};
